/**
 * @file LoadingBar.js
 * @version 2.0.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2025-09-21
 * @modified 2026-01-30
 * @description Loading bar component with animated progress
 */

import React from 'react';
import './LoadingBar.css';

/**
 * Loading Bar Component
 *
 * @param {number} progress - Progress percentage (0-100)
 * @param {string} message - Optional loading message
 * @param {string} size - Size variant: 'sm', 'md', 'lg' (default: 'md')
 * @param {boolean} indeterminate - If true, shows indeterminate animation
 * @param {boolean} showPercentage - If true, shows percentage text (default: true)
 * @param {boolean} inline - If true, uses compact inline mode
 * @param {string} className - Additional CSS classes
 */
function LoadingBar({
  progress,
  message,
  size = 'md',
  indeterminate = false,
  showPercentage = true,
  inline = false,
  className = ''
}) {
  const containerClasses = `
    loading-bar-container
    ${size !== 'md' ? size : ''}
    ${inline ? 'inline' : ''}
    ${className}
  `.trim().replace(/\s+/g, ' ');

  const isComplete = progress >= 100;
  const displayProgress = Math.min(100, Math.max(0, progress || 0));

  return (
    <div className={containerClasses}>
      {message && <div className="loading-message">{message}</div>}
      <div className={`loading-bar ${indeterminate ? 'indeterminate' : ''}`}>
        <div
          className={`loading-bar-fill ${isComplete ? 'complete' : ''}`}
          style={{ width: indeterminate ? undefined : `${displayProgress}%` }}
        />
      </div>
      {showPercentage && !indeterminate && !inline && (
        <div className="loading-percentage">{Math.round(displayProgress)}%</div>
      )}
    </div>
  );
}

export default LoadingBar;
